import React from 'react';
import { DateRange } from '../../../constants/ui';
import { UsageDetailsData } from '../../../utils/openai-api';
import type { TimezoneMode } from '../../../utils/storage';

import { UsageDataSource } from '../types';
import { formatLocalDateTime, formatTimezoneOffset } from '../utils/formatters';
import { DashboardIcon, ApiKeyIcon, InfoIcon, CurrencyIcon } from '../../ui/Icons';
import { DataSourceSelector } from './DataSourceSelector';
import { DateRangeSelector } from './DateRangeSelector';
import { ActionsMenu } from './ActionsMenu';
import { FilteredDataResult } from '../hooks/useFilteredData';

export interface DashboardHeaderProps {
  dataSource: UsageDataSource;
  setDataSource: (ds: UsageDataSource) => void;
  hasBothProviders: boolean;
  resolvedHasOpenAiKey: boolean;
  resolvedHasAnthropicKey: boolean;
  sourceDisplayName: string;
  dateRange: DateRange;
  customDateRange: { startDate: Date; endDate: Date } | null;
  getDateRange: () => { startDate: Date; endDate: Date; label: string };
  onDateRangeChange: (range: DateRange) => void;
  onCustomRangeApply: (startDate: string, endDate: string) => void;
  filteredData: FilteredDataResult | null;
  isCached: boolean;
  dataWindow: UsageDetailsData['dataWindow'];
  coverageInfo: { coverageHoursLabel: string; expectedHoursLabel: string; percentage: number } | null;
  cacheTimestamp: string | number | Date | null | undefined;
  currency: string;
  loading: boolean;
  isReloading: boolean;
  fetchUsageData: (clearCache?: boolean, isBackground?: boolean) => Promise<void>;
  timezoneMode: TimezoneMode;
  onToggleTimezone: () => void;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  dataSource,
  setDataSource,
  hasBothProviders,
  resolvedHasOpenAiKey,
  resolvedHasAnthropicKey,
  sourceDisplayName,
  dateRange,
  customDateRange,
  getDateRange,
  onDateRangeChange,
  onCustomRangeApply,
  filteredData,
  isCached,
  dataWindow,
  coverageInfo,
  cacheTimestamp,
  currency,
  loading,
  isReloading,
  fetchUsageData,
  timezoneMode,
  onToggleTimezone,
}) => {
  const offsetMinutes = dataWindow?.timezoneOffsetMinutes;
  const tzDisplayLabel = timezoneMode === 'utc'
    ? 'UTC'
    : offsetMinutes !== undefined
      ? formatTimezoneOffset(offsetMinutes)
      : 'Local';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="px-2.5 sm:px-4 py-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <div className="w-6 h-6 bg-gradient-to-br from-slate-600 to-slate-700 dark:from-slate-700 dark:to-slate-800 rounded-md flex items-center justify-center">
                <DashboardIcon className="w-4 h-4 text-white" />
              </div>
              Usage Dashboard
            </h1>
            <div className="mt-1 space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Data Source
                </span>
                <DataSourceSelector
                  dataSource={dataSource}
                  setDataSource={setDataSource}
                  hasBothProviders={hasBothProviders}
                  resolvedHasOpenAiKey={resolvedHasOpenAiKey}
                  resolvedHasAnthropicKey={resolvedHasAnthropicKey}
                  sourceDisplayName={sourceDisplayName}
                />
              </div>
              <div className="flex items-center gap-2">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {getDateRange().label}
                </p>
                {filteredData && (
                  <div className="flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800/60 rounded text-xs">
                    <ApiKeyIcon className="w-2.5 h-2.5 text-slate-500 dark:text-slate-400" />
                    <span className="text-slate-600 dark:text-slate-300 font-medium">
                      {filteredData.displayedApiKeys}/{filteredData.totalApiKeys}
                    </span>
                  </div>
                )}
                {isCached && (
                  <div className="flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 dark:bg-emerald-900/30 rounded text-xs">
                    <svg className="w-2.5 h-2.5 text-emerald-500 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-emerald-600 dark:text-emerald-300 font-medium">Cached</span>
                  </div>
                )}
              </div>
              {dataWindow && (
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400">
                  <span>
                    {formatLocalDateTime(dataWindow.start)} → {formatLocalDateTime(dataWindow.end)}
                  </span>
                  {coverageInfo && (
                    <span>
                      Coverage {coverageInfo.coverageHoursLabel}/{coverageInfo.expectedHoursLabel}h ({coverageInfo.percentage}%)
                    </span>
                  )}
                </div>
              )}
              {cacheTimestamp && (
                <div className="text-[11px] text-gray-500 dark:text-gray-500">
                  Last update: {formatLocalDateTime(cacheTimestamp)}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <DateRangeSelector
              dateRange={dateRange}
              customDateRange={customDateRange}
              onDateRangeChange={onDateRangeChange}
              onCustomRangeApply={onCustomRangeApply}
            />

            {/* Date Range Info */}
            {filteredData?.dateRangeInfo && (
              <div className="flex items-center gap-1 px-2 py-1 bg-slate-50 dark:bg-slate-800/60 rounded border border-slate-200 dark:border-slate-600">
                <InfoIcon className="w-2.5 h-2.5 text-slate-500 dark:text-slate-400" />
                <span className="text-xs text-slate-600 dark:text-slate-400">
                  {filteredData.dateRangeInfo.startDateLabel} → {filteredData.dateRangeInfo.endDateLabel}
                </span>
              </div>
            )}

            {/* Timezone Toggle */}
            <button
              onClick={onToggleTimezone}
              className="flex items-center gap-1 px-2 py-1 bg-slate-50 dark:bg-slate-800/60 rounded border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
              title={`Chart timezone: ${tzDisplayLabel}. Click to toggle.`}
            >
              <svg className="w-3 h-3 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{tzDisplayLabel}</span>
            </button>

            {/* Currency */}
            <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 dark:bg-amber-900/30 rounded border border-amber-200 dark:border-amber-700">
              <CurrencyIcon className="w-3 h-3 text-amber-600 dark:text-amber-400" />
              <span className="text-xs font-medium text-amber-700 dark:text-amber-300">{currency}</span>
            </div>

            {/* Actions */}
            <ActionsMenu
              dataSource={dataSource}
              loading={loading}
              isReloading={isReloading}
              onReload={() => fetchUsageData(true)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
